/**
 * useUpdateDatabaseConnection Hook
 *
 * Custom React Query mutation hook for updating database connections.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { databaseConnectionService } from '../../../api/database-connection.service';
import type { DatabaseConnectionUpdateDTO } from '../../../types/database-connection.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseUpdateDatabaseConnectionOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for updating database connection
 * @param tenantId - Tenant ID
 * @param dbConnectionId - Database Connection ID
 * @param options - Success/error callbacks
 */
export const useUpdateDatabaseConnection = (
  tenantId: string,
  dbConnectionId: string,
  options?: UseUpdateDatabaseConnectionOptions
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DatabaseConnectionUpdateDTO) => databaseConnectionService.updateDatabaseConnection(tenantId, dbConnectionId, data),
    onSuccess: () => {
      // Invalidate both list and details queries
      queryClient.invalidateQueries({ queryKey: ['database-connections', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['database-connections', tenantId, dbConnectionId] });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
